import React, { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { sacolaService, Sacola } from '../../services/sacola';

const SacolaTable: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { data: sacolas = [], isLoading, error } = useQuery({
    queryKey: ['sacolas'],
    queryFn: sacolaService.getAll,
  });

  const filteredSacolas = useMemo(() => {
    if (!search.trim()) return sacolas;
    const searchLower = search.toLowerCase();
    return sacolas.filter((sacola) => {
      if (sacola.id?.toString().includes(searchLower)) return true;
      if (sacola.nomeUsuario?.toLowerCase().includes(searchLower)) return true;
      if (sacola.data?.toLowerCase().includes(searchLower)) return true;
      return false;
    });
  }, [sacolas, search]);

  const paginatedSacolas = useMemo(() => {
    return filteredSacolas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredSacolas, page, rowsPerPage]);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCadastro = () => {
    navigate('/Sacola/Cadastro');
  };

  const handleVisualizar = (id: number) => {
    navigate(`/Sacola/View/${id}`);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography color="error">Erro ao carregar as sacolas.</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          placeholder="Filtrar"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 300 }}
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            },
          }}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCadastro}>
          Nova Sacola
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Quantidade</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Data inclusão</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Usuário</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSacolas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography sx={{ py: 4, color: 'text.secondary' }}>
                    {search ? 'Nenhuma sacola encontrada para o filtro informado.' : 'Nenhuma sacola cadastrada.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedSacolas.map((sacola) => (
                <TableRow key={sacola.id} hover>
                  <TableCell>{sacola.id}</TableCell>
                  <TableCell>{sacola.quantidade}</TableCell>
                  <TableCell>{sacola.data}</TableCell>
                  <TableCell>{sacola.nomeUsuario}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Visualizar">
                      <IconButton onClick={() => handleVisualizar(sacola.id)} size="small" color="primary">
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredSacolas.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} itens`}
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          '.MuiTablePagination-toolbar': { pl: 2, pr: 2 }
        }}
      />
    </Paper>
  );
};

export default SacolaTable;
